import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const reemHighlights = [
  {
    icon: "fas fa-water",
    title: "Beautiful Waterfront Paths: Enjoy long walks or cycling along the Arabian Gulf every single day.",
  },
  {
    icon: "fas fa-paw",
    title: "Pet-Friendly Spaces: Reem Central Park is a large green area perfect for your outdoor activities.",
  },
  {
    icon: "fas fa-graduation-cap",
    title: "Top Education: The island is home to prestigious schools like Repton and Paris-Sorbonne University.",
  },
  {
    icon: "fas fa-briefcase",
    title: "Close to Business: You are just a short drive away from the central business district of the city.",
  },
];

const yasHighlights = [
  {
    icon: "fas fa-ticket-alt",
    title: "Theme Park Access: Live just minutes away from Ferrari World and Warner Bros. World.",
  },
  {
    icon: "fas fa-bag-shopping",
    title: "Premier Shopping: Yas Mall offers hundreds of stores and great dining options for your convenience.",
  },
  {
    icon: "fas fa-person-biking",
    title: "Active Lifestyle: Use the Yas Marina Circuit for community exercise and enjoy pristine public beaches.",
  },
  {
    icon: "fas fa-house",
    title: "Modern Homes: Many apartments feature smart home technology and high-quality modern finishes.",
  },
];

const faqs = [
  {
    icon: "fas fa-shield-alt",
    question: "Is Abu Dhabi a safe place to rent a one bedroom apartment?",
    answer: "Yes. Abu Dhabi is consistently ranked as one of the safest cities in the world, making it a top choice for renters and families alike.",
  },
  {
    icon: "fas fa-map-marker-alt",
    question: "Which areas are best for a 1 bedroom apartment in Abu Dhabi?",
    answer: "Al Reem Island and Yas Island are two of the most popular choices, offering waterfront living, resort-style amenities, and easy access to schools, retail, and the business district.",
  },
  {
    icon: "fas fa-file-signature",
    question: "What is Tawtheeq, and why does it matter for renters?",
    answer: "Tawtheeq is the official system used to register rental contracts in Abu Dhabi. It protects your rights as a tenant and is required to activate your water and electricity services.",
  },
  {
    icon: "fas fa-handshake",
    question: "How does Nova Vision Real Estate help me find an apartment?",
    answer: "We use our deep local expertise to filter available one bedroom apartments based on your requirements, guide you through viewings, and handle the legal steps for total peace of mind.",
  },
  {
    icon: "fas fa-heart",
    question: "What amenities can I expect living in Abu Dhabi?",
    answer: "You'll have access to world-renowned healthcare such as Cleveland Clinic, modern malls like Marina Mall and Abu Dhabi Mall, and a vibrant, multicultural community.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "1 Bedroom Apartment for Rent in Abu Dhabi", item: "https://nvrealestate.ae/1-bedroom-apartment-for-rent-abu-dhabi" },
  ],
};

function OneBedroomApartmentRent() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>1 Bedroom Apartment for Rent in Abu Dhabi – Cozy Homes</title>
        <meta
          name="description"
          content="Looking for one-bedroom apartment for rent in Abu Dhabi? We filter the best apartments matching your requirements and expectations and help you make the right choice."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content="1 Bedroom Apartment for Rent in Abu Dhabi – Best Choices" />
        <meta property="og:description" content="Find your dream one bedroom apartment for rent in Abu Dhabi with Nova Vision Real Estate, from Al Reem Island to Yas Island." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nvrealestate.ae/1-bedroom-apartment-for-rent-abu-dhabi" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/seo/1-bedroom-apartment-rent/01.jpg` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi rental specialists</span>
              <h1 id="villa-page-title">1 Bedroom Apartment for Rent in Abu Dhabi – Best Choices</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=Apartment&bedrooms=1&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore apartments
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/1-bedroom-apartment-rent/02.png"
                alt="One bedroom apartments for rent in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Safest city living</strong>
                <span>Consistently ranked the safest city in the world</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Find Your Dream One Bedroom Apartment for Rent in Abu Dhabi with Nova Vision</h2>
              <p>
                Abu Dhabi is a beautiful city that offers a high quality of life for all its
                residents. It is consistently ranked as the safest city in the world, making it a
                top choice for living.
              </p>
              <p>
                If you are moving here, finding a one bedroom apartment for rent in Abu Dhabi is a
                smart move. At Nova Vision Real Estate, we use our deep expertise to help you find
                the most suitable home.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="reem-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Al Reem Island</span>
              <h2 id="reem-heading">Discover Prime Waterfront Living on Al Reem Island</h2>
              <p>
                Al Reem Island is one of the most popular places to live because of its stunning
                sea views. A 1 bedroom apartment for rent in Abu Dhabi on this island offers modern
                style and great comfort.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {reemHighlights.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              NV Real Estate knows every corner of Al Reem Island, from Shams to Marina Square. We
              guide you to the{" "}
              <Link to="/archive-properties/townhouse-for-sale-abu-dhabi/">best towers</Link> that
              offer luxury facilities like gyms and swimming pools.
            </p>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="yas-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Yas Island</span>
              <h2 id="yas-heading">Experience Endless Fun and Luxury on Yas Island</h2>
              <p>
                Yas Island is the best place for those who love excitement and a resort-style
                lifestyle. Finding a one bedroom apartment for rent in Abu Dhabi here means living
                near world-class attractions.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {yasHighlights.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              Our experts at NV Real Estate can show you exclusive units in places like Water's
              Edge. We help you find homes that provide a vacation feel while staying close to your
              work.
            </p>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="amenities-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why Nova Vision</span>
              <h2 id="amenities-heading">World-Class Amenities and Healthcare</h2>
              <p>
                Abu Dhabi offers excellent healthcare facilities such as the world-renowned
                Cleveland Clinic. You will also find many modern malls like Marina Mall and Abu
                Dhabi Mall for all your needs.
              </p>
              <p>
                Living here gives you access to a multicultural society and a very vibrant social
                scene. Nova Vision Real Estate is proud to be part of this growing and successful
                community.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Choose Nova Vision Real Estate?</h2>
                <p>
                  We are a trusted name in the market with years of experience in property
                  management. Our team provides professional advice to ensure you make the right
                  choice for your future. We handle all the hard work so you can enjoy your new
                  home without any stress.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
              </div>
              <ul>
                <li>
                  Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email
                  your requirements to us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="legal-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Tenant protection</span>
              <h2 id="legal-heading">Essential Legal Protection for Tenants</h2>
              <p>
                All rental contracts in the city must be registered with the official Tawtheeq
                system. This system protects your rights and is required to start your water and
                electricity services.
              </p>
              <p>
                Nova Vision Real Estate ensures that all legal steps are followed correctly for
                your total peace of mind. We are here to help you start your new life in Abu Dhabi
                with confidence and joy.
              </p>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Renter questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your apartment search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`apartment-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`apartment-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Homes</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Tawtheeq Support</strong><span>Registered Correctly</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.</p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default OneBedroomApartmentRent;